import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { Link } from 'react-router-dom';
import { FaCode, FaPaintBrush, FaRocket, FaMobileAlt } from 'react-icons/fa';
import './Hero.css';

const Hero = () => {
  const typeElRef = useRef(null);
  const terminalElRef = useRef(null);

  const [terminalText, setTerminalText] = useState("");
  
  useEffect(() => {
    // --- 1. Star Field Generator ---
    // Stars removed in favor of CSS Cyber Grid
    // --- 2. Headline Typewriter Effect ---
    const words = ["Startups", "Brands", "Enterprises", "Visionaries", "Founders"];
    let currentWordIndex = 0;
    let currentCharIndex = 0;
    let isDeleting = false;
    let typeTimeout;
    let active = true;
    
    function typeWriter() {
        if (!active || !typeElRef.current) return;
        const currentWord = words[currentWordIndex];
        
        if (isDeleting) {
            typeElRef.current.textContent = currentWord.substring(0, currentCharIndex - 1);
            currentCharIndex--;
        } else {
            typeElRef.current.textContent = currentWord.substring(0, currentCharIndex + 1);
            currentCharIndex++;
        }
        
        let typingSpeed = isDeleting ? 40 : 80;
        
        if (!isDeleting && currentCharIndex === currentWord.length) {
            typingSpeed = 2000;
            isDeleting = true;
        } else if (isDeleting && currentCharIndex === 0) {
            isDeleting = false;
            currentWordIndex = (currentWordIndex + 1) % words.length;
            typingSpeed = 500;
        }
        
        typeTimeout = setTimeout(typeWriter, typingSpeed);
    }

    // --- 3. Terminal Effect ---
    const terminalLogs = [
        "> initializing octalbees core...",
        "> compiling UI components...",
        "> securing data endpoints... OK",
        "> bridging API gateways... OK",
        "> deploying production build...",
        "> status: 100% ONLINE 🟢",
        "> ready for scale."
    ];
    
    let logIndex = 0;
    let charIndex = 0;
    let termTimeout;
    let currentFullText = "";

    function runTerminal() {
        if (!active) return;
        if (logIndex < terminalLogs.length) {
            const currentLine = terminalLogs[logIndex];
            currentFullText += currentLine.charAt(charIndex);
            setTerminalText(currentFullText);
            charIndex++;
            
            if (terminalElRef.current) {
                terminalElRef.current.scrollTop = terminalElRef.current.scrollHeight;
            }
            
            if (charIndex < currentLine.length) {
                termTimeout = setTimeout(runTerminal, 35);
            } else {
                currentFullText += "\n";
                charIndex = 0;
                logIndex++;
                termTimeout = setTimeout(runTerminal, 400);
            }
        } else {
            termTimeout = setTimeout(() => {
                currentFullText = "";
                setTerminalText("");
                logIndex = 0;
                charIndex = 0;
                runTerminal();
            }, 3000);
        }
    }

    // --- 4. Mouse tracking Parallax ---
    const isMobile = window.innerWidth <= 768;
    const parallaxIntensity = isMobile ? 0.5 : 1;

    const onMouseMove = (e) => {
        if (!active) return;
        const windowHalfX = window.innerWidth / 2;
        const windowHalfY = window.innerHeight / 2;
        const mouseX = (e.clientX - windowHalfX) / windowHalfX;
        const mouseY = (e.clientY - windowHalfY) / windowHalfY;

        gsap.to('.liquid-layer-1', { x: mouseX * 50 * parallaxIntensity, y: mouseY * 50 * parallaxIntensity, duration: 0.8, overwrite: 'auto' });
        gsap.to('.liquid-layer-2', { x: mouseX * 100 * parallaxIntensity, y: mouseY * 100 * parallaxIntensity, duration: 0.8, overwrite: 'auto' });
        gsap.to('.liquid-layer-3', { x: mouseX * 180 * parallaxIntensity, y: mouseY * 180 * parallaxIntensity, duration: 0.8, overwrite: 'auto' });
        gsap.to('.liquid-hero-content', { x: mouseX * 30 * parallaxIntensity, y: mouseY * 30 * parallaxIntensity, duration: 0.8, overwrite: 'auto' });
        gsap.to('.liquid-terminal', { x: mouseX * 60 * parallaxIntensity, y: mouseY * 60 * parallaxIntensity, duration: 0.8, overwrite: 'auto' });
    };
    document.addEventListener('mousemove', onMouseMove);

    // --- 5. GSAP Entrance ---
    const tl = gsap.timeline();

    tl.to('.cyber-grid-container', { opacity: 1, duration: 1.2 })
      .to('.liquid-word', { y: '0%', duration: 0.6, ease: "power3.out", stagger: 0.15 }, "-=0.4")
      .call(() => {
          if(active) setTimeout(typeWriter, 500);
      })
      .to('.liquid-subtitle', { opacity: 1, y: -5, duration: 0.5, ease: "power2.out" }, "-=0.1")
      .to('.liquid-terminal', { x: 0, duration: 0.6, ease: "power3.out", onComplete: () => { if(active) runTerminal(); } }, "-=0.2")
      .to('.liquid-floating-icon', { opacity: 1, duration: 0.4, stagger: 0.1 }, "-=0.4")
      .to('.liquid-cta-group', { opacity: 1, y: -10, duration: 0.5 }, "-=0.2")
      .to('.liquid-scroll-indicator', { opacity: 1, duration: 0.5 }, "-=0.2");

    // Removed JS stars resize logic

    return () => {
        active = false;
        document.removeEventListener('mousemove', onMouseMove);
        clearTimeout(typeTimeout);
        clearTimeout(termTimeout);
        if (tl) tl.kill();
    };
  }, []);

  return (
    <section className="liquid-hero" id="hero">
        <div className="cyber-grid-container">
            <div className="cyber-grid"></div>
            <div className="cyber-grid-fade"></div>
        </div>
        
        {/* Animated Background Orbs */}
        <div className="mobile-glow-orb orb-1"></div>
        <div className="mobile-glow-orb orb-2"></div>
        
        <div className="liquid-floating-icon liquid-icon-tl"><FaCode color="#7B5CF5" /></div>
        <div className="liquid-floating-icon liquid-icon-tr"><FaRocket color="#ea580c" /></div>
        <div className="liquid-floating-icon liquid-icon-bl"><FaMobileAlt color="#27c93f" /></div>
        <div className="liquid-floating-icon liquid-icon-br"><FaPaintBrush color="#ff5f56" /></div>

        <div className="liquid-hero-content">
            <h1 className="liquid-headline">
                <span className="liquid-word-wrap"><span className="liquid-word">We</span></span>{' '}
                <span className="liquid-word-wrap"><span className="liquid-word">Build</span></span>{' '}
                <span className="liquid-word-wrap"><span className="liquid-word">Digital</span></span><br/>
                <span className="liquid-word-wrap"><span className="liquid-word">Legacies</span></span>{' '}
                <span className="liquid-word-wrap"><span className="liquid-word">For</span></span><br/>
                <span className="liquid-word-wrap">
                    <span className="liquid-word">
                        <span className="liquid-typewriter-container">
                            <span ref={typeElRef}></span><span className="liquid-cursor">|</span>
                        </span>
                    </span>
                </span>
            </h1>
            <p className="liquid-subtitle">Your product. Our engineering. Shipped faster than you thought possible.</p>
            <div className="liquid-cta-group">
                <Link to="/contact-us" className="liquid-btn liquid-btn-primary">⚡ Get Started</Link>
                <Link to="/services" className="liquid-btn liquid-btn-ghost">See Our Work →</Link>
            </div>
        </div>

        <div className="liquid-terminal">
            <div className="liquid-terminal-header">
                <div className="liquid-dot liquid-red"></div>
                <div className="liquid-dot liquid-yellow"></div>
                <div className="liquid-dot liquid-green"></div>
            </div>
            <div className="liquid-terminal-body" ref={terminalElRef}>
                <span style={{ whiteSpace: 'pre-wrap', color: '#c4c8cc', fontFamily: "'JetBrains Mono', monospace" }}>{terminalText}</span>
                <span className="liquid-cursor">█</span>
            </div>
        </div>

        <div className="liquid-scroll-indicator">
            <div className="liquid-mouse"><div className="liquid-wheel"></div></div>
        </div>
    </section>
  );
};

export default Hero;
